import React, { useRef } from 'react';
import { CalendarIcon } from '@/../public/assets/icons';
import { signupStyles } from '@/components/features/registration/signup-templates/signup.styles';
import { Box, Stack, Button, Field, Input, createListCollection, Select, Portal } from '@chakra-ui/react';
import PhoneInput from 'react-phone-input-2';
import { personalDetailsSchema, TPersonalDetails } from './personal-details.validator';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import ReactFlagsSelect from 'react-flags-select';
import { PersonalDetailsProps } from '../../templates';
import { Gender } from '@/core/models';

const PersonalDetails = ({ next, details }: PersonalDetailsProps) => {
   const dateInputRef = useRef<HTMLInputElement | null>(null);
   const openDatePicker = () => {
      if (dateInputRef.current) {
         dateInputRef.current.showPicker();
      }
   };

   const {
      register,
      handleSubmit,
      reset,
      setValue,
      watch,
      formState: { errors },
      control,
   } = useForm<TPersonalDetails>({
      defaultValues: details,
      resolver: zodResolver(personalDetailsSchema),
   });

   const onSubmit = handleSubmit(async data => {
      next(data);
   });

   const genderOptions = createListCollection({
      items: Object.values(Gender || {}).map(item => ({ label: item.replaceAll('_', ' '), value: item })),
   });

   const { phone_number, nationality } = watch();
   return (
      <Box as="form" css={signupStyles.signup_form} onSubmit={onSubmit}>
         <Stack gap="4" align="flex-start" minW="full">
            <Field.Root invalid={!!errors.gender}>
               <Field.Label textStyle="xs" color="white" mb="1">
                  Gender
                  <Box as="span" color="error">
                     *
                  </Box>
               </Field.Label>
               <Controller
                  control={control}
                  name="gender"
                  render={({ field }) => (
                     <Select.Root
                        bg="#2D2D2D"
                        borderRadius="12px"
                        name={field.name}
                        value={[field.value.toString()]}
                        onValueChange={({ value }) => field.onChange(value?.[0])}
                        onInteractOutside={() => field.onBlur()}
                        collection={genderOptions}
                     >
                        <Select.HiddenSelect />
                        <Select.Control>
                           <Select.Trigger border="none">
                              <Select.ValueText placeholder="Select" textTransform="capitalize">
                                 {field.value
                                    ? genderOptions.items.find(item => item.value === field.value)?.label
                                    : 'Select'}
                              </Select.ValueText>
                           </Select.Trigger>
                           <Select.IndicatorGroup>
                              <Select.Indicator />
                           </Select.IndicatorGroup>
                        </Select.Control>
                        <Portal>
                           <Select.Positioner>
                              <Select.Content bg="#2D2D2D">
                                 {genderOptions.items.map(item => (
                                    <Select.Item
                                       item={item}
                                       key={item.value}
                                       textTransform="capitalize"
                                       color="white"
                                       cursor="pointer"
                                       _hover={{ bg: '#3B3B3B' }}
                                    >
                                       {item.label}
                                       <Select.ItemIndicator />
                                    </Select.Item>
                                 ))}
                              </Select.Content>
                           </Select.Positioner>
                        </Portal>
                     </Select.Root>
                  )}
               />
            </Field.Root>
            <Field.Root invalid={Boolean(errors.address_1?.message)}>
               <Field.Label textStyle="xs" color="white" mb="1">
                  Address Line 1
                  <Box as="span" color="error">
                     *
                  </Box>
               </Field.Label>
               <Input
                  placeholder="Address Line 1"
                  variant="subtle"
                  h="10"
                  rounded="12px"
                  bg="#202020"
                  {...register('address_1')}
               />
               <Field.ErrorText>{errors.address_1?.message}</Field.ErrorText>
            </Field.Root>
            <Field.Root invalid={Boolean(errors.address_2?.message)}>
               <Field.Label textStyle="xs" color="white" mb="1">
                  Address Line 2
               </Field.Label>
               <Input
                  placeholder="Address Line 2"
                  variant="subtle"
                  h="10"
                  rounded="12px"
                  bg="#202020"
                  {...register('address_2')}
               />
               <Field.ErrorText>{errors.address_2?.message}</Field.ErrorText>
            </Field.Root>
            <Box css={signupStyles.double_input}>
               <Field.Root invalid={Boolean(errors.post_code?.message)}>
                  <Field.Label textStyle="xs" color="white" mb="1">
                     Post Code
                     <Box as="span" color="error">
                        *
                     </Box>
                  </Field.Label>
                  <Input
                     placeholder="Post Code"
                     variant="subtle"
                     h="10"
                     rounded="12px"
                     bg="#202020"
                     {...register('post_code')}
                  />
                  <Field.ErrorText>{errors.post_code?.message}</Field.ErrorText>
               </Field.Root>

               <Field.Root invalid={Boolean(errors.phone_number?.message)}>
                  <Field.Label textStyle="xs" color="white" mb="1">
                     Phone Number
                     <Box as="span" color="error">
                        *
                     </Box>
                  </Field.Label>
                  <PhoneInput
                     country={'gb'}
                     preferredCountries={['gb', 'us']}
                     placeholder="Phone Number"
                     enableSearch
                     value={phone_number}
                     onChange={v => setValue('phone_number', v)}
                     inputStyle={{
                        width: '100%',
                        height: '40px',
                        background: '#202020',
                        borderRadius: '12px',
                        border: errors.phone_number?.message ? '1px solid #f87171' : 'none',
                        color: 'white',
                        paddingLeft: '46px',
                        fontSize: '14px',
                     }}
                     dropdownStyle={{
                        backgroundColor: '#000',
                        color: 'white',
                     }}
                  />
                  <Field.ErrorText>{errors.phone_number?.message}</Field.ErrorText>
               </Field.Root>
            </Box>
            <Box css={signupStyles.double_input}>
               <Field.Root invalid={Boolean(errors.date_of_birth?.message)}>
                  <Field.Label textStyle="xs" color="white" mb="1">
                     Date Of Birth
                     <Box as="span" color="error">
                        *
                     </Box>
                  </Field.Label>
                  <Box position="relative" w="full" onClick={openDatePicker}>
                     <Box css={signupStyles.icon_box}>
                        <CalendarIcon />
                     </Box>
                     <Input
                        css={signupStyles.date_input}
                        type="date"
                        placeholder="Date Of Birth"
                        variant="subtle"
                        h="10"
                        rounded="12px"
                        bg="#202020"
                        {...register('date_of_birth')}
                        ref={e => {
                           register('date_of_birth').ref(e);
                           dateInputRef.current = e;
                        }}
                     />
                  </Box>
                  <Field.ErrorText>{errors.date_of_birth?.message}</Field.ErrorText>
               </Field.Root>

               <Field.Root invalid={Boolean(errors.nhs_number?.message)}>
                  <Field.Label textStyle="xs" color="white" mb="1">
                     NHS Number
                     <Box as="span" color="error">
                        *
                     </Box>
                  </Field.Label>
                  <Input
                     type="number"
                     css={signupStyles.num_input}
                     placeholder="NHS Number"
                     variant="subtle"
                     h="10"
                     rounded="12px"
                     bg="#202020"
                     {...register('nhs_number')}
                  />
                  <Field.ErrorText>{errors.nhs_number?.message}</Field.ErrorText>
               </Field.Root>
            </Box>
            <Box css={signupStyles.double_input}>
               <Field.Root invalid={Boolean(errors.state?.message)}>
                  <Field.Label textStyle="xs" color="white" mb="1">
                     Town
                     <Box as="span" color="error">
                        *
                     </Box>
                  </Field.Label>
                  <Input
                     type="text"
                     css={signupStyles.num_input}
                     placeholder="Town"
                     variant="subtle"
                     h="10"
                     rounded="12px"
                     bg="#202020"
                     {...register('state')}
                  />
                  <Field.ErrorText>{errors.state?.message}</Field.ErrorText>
               </Field.Root>

               <Field.Root invalid={Boolean(errors.city?.message)}>
                  <Field.Label textStyle="xs" color="white" mb="1">
                     City
                     <Box as="span" color="error">
                        *
                     </Box>
                  </Field.Label>
                  <Input
                     type="text"
                     css={signupStyles.num_input}
                     placeholder="City"
                     variant="subtle"
                     h="10"
                     rounded="12px"
                     bg="#202020"
                     {...register('city')}
                  />
                  <Field.ErrorText>{errors.city?.message}</Field.ErrorText>
               </Field.Root>
            </Box>
            <Field.Root invalid={Boolean(errors.nationality?.message)}>
               <Field.Label textStyle="xs" color="white" mb="1">
                  Residence
                  <Box as="span" color="error">
                     *
                  </Box>
               </Field.Label>
               <Box
                  css={{
                     ...signupStyles.resident_input,
                     ...(errors.nationality?.message ? signupStyles.resident_input_error : {}),
                  }}
               >
                  <ReactFlagsSelect
                     selected={nationality}
                     onSelect={code => setValue('nationality', code)}
                     searchable
                     fullWidth
                     countries={['GB']}
                  />
               </Box>

               <Field.ErrorText>{errors.nationality?.message}</Field.ErrorText>
            </Field.Root>
            <Box css={signupStyles.signup_btn_con}>
               <Button
                  type="reset"
                  onClick={() => reset()}
                  bg="darkerBtn"
                  textStyle="xs"
                  color="white"
                  h="10"
                  maxW="36"
                  minW="36"
                  p="0"
               >
                  Reset
               </Button>
               <Button type="submit" textStyle="xs" h="10" maxW="36" minW="36" p="0">
                  Next
               </Button>
            </Box>
         </Stack>
      </Box>
   );
};

export default PersonalDetails;
